/**
 * W-14 — the ONE home for the fueling-profile + box-cadence question copy. The
 * wizard's step components consume these; JourneySettingsModal and
 * Preferences/FuelingSection are wired to the same constants so the athlete is
 * never asked the same question in different words on different surfaces.
 */

/** Box cadence ("How often should a box arrive?"). */
export const CADENCE_QUESTION = {
  title: "How often should a box arrive?",
  desc: "Fuel is generated and delivered in boxes, matched to your latest training. Nothing upfront."
} as const;

/** Gut practice — the one required fueling answer. */
export const GUT_QUESTION = {
  label: "How often do you eat or drink carbs during workouts longer than an hour?",
  desc: "Gels, chews, sports drink, real food — anything counts.",
  options: [
    {
      value: "new",
      title: "This is new to me",
      desc: "I rarely take fuel during workouts, or no consistent routine yet."
    },
    {
      value: "tuning",
      title: "Some, I'm still fine-tuning",
      desc: "I use fuel during workouts, but I'm still dialing in what works."
    },
    {
      value: "locked",
      title: "I'm locked in on a carbs-per-hour number",
      desc: "I fuel regularly and have a clear target that works for me."
    }
  ]
} as const;

/** Sweat → sodium proxy. "Not sure" stays a pickable choice (D-11 only stops
 *  it being pre-selected). `desc` is currently rendered on the Preferences
 *  surface only (the wizard's About-your-body step keeps its own subtitle);
 *  it lives here so the wording has ONE home if the wizard ever shows it. */
export const SWEAT_QUESTION = {
  label: "How salty do things get when you sweat?",
  desc: "Sets the sodium we pack alongside your carbs — saltier sweat, more electrolytes.",
  options: [
    {
      value: "unsure",
      title: "Not sure",
      desc: "Fine for most people — we'll use an average sodium plan, easy to tune later."
    },
    { value: "lightly", title: "Lightly salted", desc: "No salt on skin, clothes, or hat." },
    {
      value: "somewhat",
      title: "Somewhat salty",
      desc: "Salt marks after long, hot or hard efforts."
    },
    {
      value: "pretzel",
      title: "Full pretzel mode",
      desc: "White streaks and gritty skin, most days."
    }
  ]
} as const;

/** Allergens & must-avoids. `emptyNote` is the neutral empty state (W-06). */
export const ALLERGENS_QUESTION = {
  label: "Allergens & must-avoids",
  desc: "We never put products with these in your boxes.",
  emptyNote: "No allergens selected — we'll consider every product."
} as const;

export const FLAVORS_PREFER_QUESTION = {
  label: "Flavors you prefer",
  desc: "We lean toward these where we can."
} as const;

export const FLAVORS_AVOID_QUESTION = {
  label: "Flavors to avoid",
  desc: "We keep these out of your boxes where we can."
} as const;
